import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import * as React from "react";
import {useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {useMutation} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {RequestInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";

import RequireReauth from "../require-reauth.tsx";

import type {
    requestAuthEmailForDevType,
    requestAuthEmailType,
} from "./__graphql-types__/index.ts";

type ErrorCode =
    | "REAUTH_REQUIRED"
    | "INVALID_EMAIL"
    | "ADDRESS_ALREADY_LINKED_TO_ACCOUNT"
    | "ADDRESS_ALREADY_LINKED_TO_ANOTHER_ACCOUNT";

// The first two strings don't require an email input but it seems cleaner to
// have a mapping with every entry having the same form than a bunch of if statements.
const ERROR_CODE_MESSAGE_MAPPING: Partial<
    Record<ErrorCode, (arg1: string) => string>
> = {
    REAUTH_REQUIRED: (email: string) =>
        t`Reauthentication required to perform this action. Please try again.`,
    INVALID_EMAIL: (email: string) =>
        t`Please enter a valid email address in order to link it to your Khan Academy account.`,
    ADDRESS_ALREADY_LINKED_TO_ACCOUNT: (email: string) =>
        t`The email address ${email} is already linked to your Khan Academy account.`,
    ADDRESS_ALREADY_LINKED_TO_ANOTHER_ACCOUNT: (email: string) =>
        t`Sorry, ${email} is already linked to another Khan Academy account. Please log into it and unlink the email address or delete the account in order to link the email address to this account.`,
};

interface Props {
    buttonText: string;
    identifier: string | null | undefined;
}

const MUTATION: GqlOperation<
    requestAuthEmailType["response"],
    requestAuthEmailType["variables"]
> = gqlOp(gql`
    mutation requestAuthEmail($authEmail: String!) {
        requestAuthEmail(authEmail: $authEmail) {
            user {
                id
                pendingEmails
            }
            error {
                code
            }
        }
    }
`);

const MUTATION_DEV: GqlOperation<
    requestAuthEmailForDevType["response"],
    requestAuthEmailForDevType["variables"]
> = gqlOp(gql`
    mutation requestAuthEmailForDev($authEmail: String!) {
        requestAuthEmail(authEmail: $authEmail) {
            user {
                id
                pendingEmails
            }
            verificationUrl
            error {
                code
            }
        }
    }
`);

const isDevResponse = (
    candidate: any,
): candidate is requestAuthEmailForDevType["response"] => {
    if (candidate.requestAuthEmail.verificationUrl) {
        return true;
    }
    return false;
};

export const ConnectEmail: React.FC<Props> = ({buttonText, identifier}) => {
    const [reauthSuccessful, setReauthSuccessful] = useState(false);
    const [emailInput, setEmailInput] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [hideErrorOrSuccessMessage, setHideErrorOrSuccessMessage] =
        useState(false);

    const [requestEmailMutation, result] = useMutation(
        RequestInfo.IS_DEV_SERVER ? MUTATION_DEV : MUTATION,
    );

    const handleMutationCompletion = (
        data?: requestAuthEmailType["response"] | null,
    ) => {
        if (data?.requestAuthEmail && !data.requestAuthEmail.error) {
            if (RequestInfo.IS_DEV_SERVER && isDevResponse(data)) {
                setSuccessMessage(
                    t`Dev-only verification link: ${data.requestAuthEmail.verificationUrl ?? ""}`,
                );
            } else {
                setSuccessMessage(
                    t`Please check your email at ${emailInput} and click on the link to finish connecting your email address.`,
                );
            }
        }
    };

    const handleEmailInputChange = (newEmail: string) => {
        setEmailInput(newEmail);
        setHideErrorOrSuccessMessage(true);
    };

    const handleSendConfirmationEmail = async () => {
        const mutationResult = await requestEmailMutation({
            authEmail: emailInput,
        });

        setHideErrorOrSuccessMessage(false);

        if (mutationResult.status === "success") {
            handleMutationCompletion(mutationResult.data);
        }
    };

    const getModal = () => {
        let errorMessage: string | null = null;

        if (result?.status === "error") {
            errorMessage = t`Oops. Something went wrong. Please try again.`;
        }

        if (
            result?.status === "success" &&
            result.data.requestAuthEmail?.error?.code
        ) {
            const errorFunction =
                ERROR_CODE_MESSAGE_MAPPING[
                    result.data.requestAuthEmail.error.code
                ];
            errorMessage = errorFunction
                ? errorFunction(emailInput)
                : t`Oops. Something went wrong. Please try again.`;
        }

        const termsOfService = (
            <a href="/about/docs/khan-academy-terms-of-service" target="_blank">
                {t`Terms of Service`}
            </a>
        );

        const privacyPolicy = (
            <a href="/about/privacy-policy" target="_blank">
                {t`Privacy Policy`}
            </a>
        );

        return (
            <OnePaneDialog
                title={t`Link an email address`}
                style={styles.modal}
                content={
                    <div className={css(styles.modalBody)}>
                        <p className={css(styles.modalText)}>
                            <Trans>
                                Enter your new email address and we'll send a
                                confirmation email for you to open. By adding an
                                email address you agree to our {termsOfService}{" "}
                                and {privacyPolicy}.
                            </Trans>
                        </p>
                        <label className={css(styles.emailLabel)}>
                            {t`Email address`}
                        </label>
                        <TextField
                            id="connect-email-input"
                            type="email"
                            onChange={handleEmailInputChange}
                            value={emailInput}
                        />
                        {errorMessage && !hideErrorOrSuccessMessage && (
                            // TODO(hack25-darkmode): Change to Banner.critical
                            <Body style={styles.errorMessage}>
                                {errorMessage}
                            </Body>
                        )}
                        {successMessage && !hideErrorOrSuccessMessage && (
                            // TODO(hack25-darkmode): Change to Banner.success
                            <Body style={styles.successMessage}>
                                {successMessage}
                            </Body>
                        )}
                    </div>
                }
                footer={
                    <Button
                        onClick={handleSendConfirmationEmail}
                        spinner={result?.status === "loading"}
                    >
                        {t`Send confirmation email`}
                    </Button>
                }
            />
        );
    };

    const ariaLabel = `${buttonText} ${t`(Opens a modal)`}`;

    return (
        <RequireReauth
            identifier={identifier}
            onSuccess={() => setReauthSuccessful(true)}
            onFailure={() => {}}
        >
            {(invokeReauth) => (
                <React.Fragment>
                    <ModalLauncher
                        initialFocusId="connect-email-input"
                        opened={reauthSuccessful}
                        onClose={() => setReauthSuccessful(false)}
                        modal={getModal()}
                    />
                    <Button
                        onClick={invokeReauth}
                        aria-label={ariaLabel}
                        style={styles.button}
                        size="small"
                        kind="tertiary"
                    >
                        {buttonText}
                    </Button>
                </React.Fragment>
            )}
        </RequireReauth>
    );
};

const styles = StyleSheet.create({
    modal: {
        maxWidth: "50%",
        height: "auto",

        [mediaQueries.smOrSmaller]: {
            minInlineSize: 0,
            width: "100%",
            height: "100%",
            borderRadius: 0,
        },
    },
    modalBody: {
        marginBlockEnd: 0,
    },
    modalText: {
        marginBlockStart: 0,
    },
    button: {
        alignSelf: "flex-start",
    },
    emailLabel: {
        paddingInlineEnd: sizing.size_200,
    },
    errorMessage: {
        paddingBlockStart: sizing.size_200,
        color: semanticColor.core.foreground.critical.default,
    },
    successMessage: {
        paddingBlockStart: sizing.size_200,
        color: semanticColor.core.foreground.success.default,
    },
});

export default ConnectEmail;
